import type {
  FileProcessResultResponse,
  GetPresignedUrlRequestResponse,
} from '@/api';
import { ApiError } from '@/api/middlewares/error-toast-middleware';
import { useNotification } from '@/hooks/use-notification';
import { type UploadHandle, uploadFileToPresignedUrl } from '@/pages/queries';
import Uppy from '@uppy/core';
import DropTarget from '@uppy/drop-target';
import { useCallback, useEffect, useRef, useState } from 'react';

const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export enum UploadMode {
  Default = 'Default',
  Dragging = 'Dragging',
  FileDropped = 'FileDropped',
  UploadInProgress = 'UploadInProgress',
  Processing = 'Processing',
  UploadFinished = 'UploadFinished',
}

export type FileUploadResult = {
  uploadResult: FileProcessResultResponse | undefined;
  errorMessage: string | null;
};

type UseFileUploadArgs = {
  /** Presigned-url target. Required for S3 upload flow. */
  target?: string;
  /** Presign request for this upload flow. */
  getPresignedPutUrl: (
    fileName: string,
  ) => Promise<GetPresignedUrlRequestResponse>;
  /** Fetches the processing report once the file has been uploaded to S3 */
  getReport?: (
    fileId: string,
  ) => Promise<FileProcessResultResponse | undefined>;
  /** Local processing function — skips S3 upload entirely. */
  onLocalProcess?: (file: File) => Promise<FileUploadResult>;
  onUploadFinished?: (result: FileUploadResult) => void;
};

type UploadReportErrorPayload = {
  fileProcessResult?: FileProcessResultResponse;
  detail?: string;
  message?: string;
};

const resolveError = (error: unknown): FileUploadResult => {
  if (error instanceof ApiError) {
    const { fileProcessResult, detail, message } =
      (error.details as UploadReportErrorPayload) ?? {};
    return {
      uploadResult:
        fileProcessResult ?? (error.details as FileProcessResultResponse),
      errorMessage:
        typeof detail === 'string'
          ? detail
          : typeof message === 'string'
            ? message
            : error.message,
    };
  }
  if (error instanceof Error) {
    return { uploadResult: undefined, errorMessage: error.message };
  }
  return {
    uploadResult: undefined,
    errorMessage: 'An unexpected error occurred. Please try again.',
  };
};

export const useFileUpload = ({
  target,
  getPresignedPutUrl,
  getReport,
  onLocalProcess,
  onUploadFinished,
}: UseFileUploadArgs) => {
  const showNotification = useNotification();
  const isLocalMode = !!onLocalProcess;

  const [mode, setMode] = useState<UploadMode>(UploadMode.Default);
  const [file, setFile] = useState<File | undefined>();
  const [uploadUrl, setUploadUrl] = useState<string | null>(null);
  const [uploadLogId, setUploadLogId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStartTime, setUploadStartTime] = useState<number | null>(null);
  const [uploadResult, setUploadResult] = useState<
    FileProcessResultResponse | undefined
  >(undefined);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const uploadHandleRef = useRef<UploadHandle | null>(null);
  const uploadCancelledRef = useRef(false);

  const reset = useCallback(() => {
    setUploadResult(undefined);
    setErrorMessage(null);
    setFile(undefined);
    setUploadUrl(null);
    setUploadLogId(null);
    setUploadProgress(0);
    setUploadStartTime(null);
    uploadHandleRef.current = null;
    setMode(UploadMode.Default);
  }, []);

  const processFile = useCallback(
    (incomingFile: File): boolean => {
      if (incomingFile.type !== XLSX_MIME) {
        showNotification({
          type: 'error',
          title: 'File upload failed',
          description: 'Only .xlsx files are allowed.',
        });
        return false;
      }

      if (isLocalMode) {
        setFile(incomingFile);
        setMode(UploadMode.FileDropped);
        return true;
      }

      getPresignedPutUrl(incomingFile.name)
        .then(({ uploadUrl: nextUploadUrl, logId }) => {
          setUploadUrl(nextUploadUrl);
          setUploadLogId(logId);
        })
        .catch(() => {
          showNotification({
            type: 'error',
            title: 'Upload preparation failed',
            description: 'Could not prepare the upload. Please try again.',
          });
          setFile(undefined);
          setMode(UploadMode.Default);
        });
      setFile(incomingFile);
      setMode(UploadMode.FileDropped);
      return true;
    },
    [getPresignedPutUrl, showNotification, isLocalMode],
  );

  const upload = useCallback(async () => {
    if (!file) return;

    if (isLocalMode && onLocalProcess) {
      setMode(UploadMode.Processing);
      try {
        const result = await onLocalProcess(file);
        setUploadResult(result.uploadResult);
        setErrorMessage(result.errorMessage);
        onUploadFinished?.(result);
      } catch (error) {
        const resolved = resolveError(error);
        setUploadResult(resolved.uploadResult);
        setErrorMessage(resolved.errorMessage);
        onUploadFinished?.(resolved);
      } finally {
        setMode(UploadMode.UploadFinished);
      }
      return;
    }

    if (!uploadUrl || !uploadLogId || !target || !getReport) return;

    uploadCancelledRef.current = false;
    setMode(UploadMode.UploadInProgress);
    setUploadProgress(0);
    setUploadStartTime(Date.now());

    try {
      const handle = uploadFileToPresignedUrl({
        file,
        uploadUrl,
        logId: uploadLogId,
        target,
        onProgress: setUploadProgress,
      });
      uploadHandleRef.current = handle;
      await handle.promise;
      if (uploadCancelledRef.current) return;
      setUploadProgress(100);
      setMode(UploadMode.Processing);
      const result = await getReport(uploadLogId);
      if (uploadCancelledRef.current) return;
      setUploadResult(result);
      onUploadFinished?.({ uploadResult: result, errorMessage: null });
    } catch (error) {
      const resolved = resolveError(error);
      setUploadResult(resolved.uploadResult);
      setErrorMessage(resolved.errorMessage);
      onUploadFinished?.(resolved);
    } finally {
      uploadHandleRef.current = null;
      if (!uploadCancelledRef.current) {
        setMode(UploadMode.UploadFinished);
      }
    }
  }, [
    file,
    uploadUrl,
    uploadLogId,
    target,
    getReport,
    onLocalProcess,
    isLocalMode,
    onUploadFinished,
  ]);

  const cancelUpload = useCallback(() => {
    uploadCancelledRef.current = true;
    if (uploadHandleRef.current) {
      uploadHandleRef.current.cancel();
      uploadHandleRef.current = null;
    }
    reset();
  }, [reset]);

  const cancelInFlight = useCallback(() => {
    if (uploadHandleRef.current) {
      uploadHandleRef.current.cancel();
    }
  }, []);

  return {
    // state
    mode,
    file,
    uploadUrl,
    uploadLogId,
    uploadProgress,
    uploadStartTime,
    uploadResult,
    errorMessage,
    canUpload: isLocalMode || (!!uploadUrl && !!uploadLogId),
    // setters used by drag handling
    setMode,
    setFile,
    // actions
    processFile,
    upload,
    cancelUpload,
    cancelInFlight,
    reset,
  };
};

/**
 * Attaches an Uppy DropTarget to `element` while `enabled`. Calls `onFile`
 * whenever a valid file is dropped. Optionally reports drag-over/leave so the
 * consumer can render a "drop here" state.
 */
export const useUppyDropTarget = ({
  element,
  enabled,
  onFile,
  onDragStateChange,
}: {
  element: HTMLElement | null;
  enabled: boolean;
  onFile: (file: File) => void;
  onDragStateChange?: (isDragging: boolean) => void;
}) => {
  useEffect(() => {
    if (!enabled || !element) return;

    const uppy = new Uppy({
      restrictions: {
        maxNumberOfFiles: 1,
        allowedFileTypes: ['.xlsx', XLSX_MIME],
      },
    });

    uppy.use(DropTarget, {
      target: element,
      onDragOver: () => onDragStateChange?.(true),
      onDragLeave: () => onDragStateChange?.(false),
      onDrop: () => onDragStateChange?.(false),
    });

    uppy.on('file-added', (addedFile) => {
      if (addedFile.data instanceof File) {
        onFile(addedFile.data);
      }
      uppy.clear();
    });

    return () => {
      uppy.destroy();
    };
  }, [element, enabled, onFile, onDragStateChange]);
};
